import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";

import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { toast } from "react-toastify";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  listData,
  onSuccess,
}) => {
  // State for form data
  const [formData, setFormData] = useState({
    nameAr: "",
    nameEn: "",
    descriptionAr: "",
    descriptionEn: "",
    previewDescAr: "",
    previewDescEn: "",
  });

  // State for UI control
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Fill form data when editing an existing list
  useEffect(() => {
    if (listData && currentView === "editList" && open) {
      setFormData({
        nameAr: listData.name?.ar || "",
        nameEn: listData.name?.en || "",
        descriptionAr: listData.description?.ar || "",
        descriptionEn: listData.description?.en || "",
        previewDescAr: listData.previewDesc?.ar || "",
        previewDescEn: listData.previewDesc?.en || "",
      });
    }
  }, [listData, currentView, open]);

  const resetForm = () => {
    setFormData({
      nameAr: "",
      nameEn: "",
      descriptionAr: "",
      descriptionEn: "",
      previewDescAr: "",
      previewDescEn: "",
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.nameAr || formData.nameAr.trim().length < 2) {
      newErrors.nameAr =
        "اسم القائمة بالعربي مطلوب ويجب أن يكون على الأقل حرفين";
    }

    if (!formData.nameEn || formData.nameEn.trim().length < 2) {
      newErrors.nameEn =
        "اسم القائمة بالانجليزية مطلوب ويجب أن يكون على الأقل حرفين";
    }

    if (!formData.descriptionAr || formData.descriptionAr.trim().length === 0) {
      newErrors.descriptionAr = "وصف القائمة بالعربي مطلوب";
    }

    if (!formData.descriptionEn || formData.descriptionEn.trim().length === 0) {
      newErrors.descriptionEn = "وصف القائمة بالانجليزية مطلوب";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const prepareFormData = () => {
    const data = {
      name: {
        ar: formData.nameAr.trim(),
        en: formData.nameEn.trim(),
      },
      description: {
        ar: formData.descriptionAr.trim(),
        en: formData.descriptionEn.trim(),
      },
    };

    // For edit operations, always include previewDesc to handle clearing
    // For create operations, only include if there's content
    if (currentView === "editList") {
      // Always include previewDesc for updates (even if empty) to allow clearing
      data.previewDesc = {
        ar: formData.previewDescAr.trim(),
        en: formData.previewDescEn.trim(),
      };
    } else if (currentView === "createList") {
      // Only add previewDesc for creation if at least one field has content
      if (formData.previewDescAr.trim() || formData.previewDescEn.trim()) {
        data.previewDesc = {
          ar: formData.previewDescAr.trim(),
          en: formData.previewDescEn.trim(),
        };
      }
    }

    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      const requestData = prepareFormData();

      if (currentView === "createList") {
        // Create new list
        response = await api.post("/package-lists", requestData);
        toast.success("تم إضافة القائمة بنجاح");
      } else if (currentView === "editList" && listData) {
        // Update existing list
        response = await api.patch(
          `/package-lists/${listData._id}`,
          requestData
        );
        toast.success("تم تحديث بيانات القائمة بنجاح");
      }

      if (response && response.data.status === "success") {
        resetForm();
        onSuccess(); // Refresh lists
        onClose(); // Close modal
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      // Handle different error types
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        const errorMessage =
          currentView === "createList"
            ? "حدث خطأ أثناء إضافة القائمة"
            : "حدث خطأ أثناء تحديث القائمة";
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderListForm = () => (
    <div className="user-modal">
      <p className="user-modal-p">
        {currentView === "createList"
          ? "اضافة قائمة جديدة"
          : "تعديل بيانات القائمة"}
      </p>
      <form onSubmit={handleSubmit}>
        {/* اسم القائمة بالعربي */}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="nameAr"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم القائمة بالعربي
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="nameAr"
            name="nameAr"
            type="text"
            value={formData.nameAr}
            onChange={handleInputChange}
            notched
            error={!!errors.nameAr}
            placeholder="مثال: المنازل"
          />
          {errors.nameAr && <p className="error-message">{errors.nameAr}</p>}
        </FormControl>

        {/* اسم القائمة بالانجليزية */}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="nameEn"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            اسم القائمة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="nameEn"
            name="nameEn"
            type="text"
            value={formData.nameEn}
            onChange={handleInputChange}
            notched
            error={!!errors.nameEn}
            placeholder="Example: Homes"
          />
          {errors.nameEn && <p className="error-message">{errors.nameEn}</p>}
        </FormControl>

        {/* وصف القائمة بالعربي */}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="descriptionAr"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            وصف القائمة بالعربي
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="descriptionAr"
            name="descriptionAr"
            type="text"
            multiline
            rows={3}
            value={formData.descriptionAr}
            onChange={handleInputChange}
            notched
            error={!!errors.descriptionAr}
            placeholder="مثال: باقات المنازل"
          />
          {errors.descriptionAr && (
            <p className="error-message">{errors.descriptionAr}</p>
          )}
        </FormControl>

        {/* وصف القائمة بالانجليزية */}
        <FormControl sx={{ width: "100%" }} variant="outlined" required>
          <InputLabel
            htmlFor="descriptionEn"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            وصف القائمة بالانجليزية
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            required
            id="descriptionEn"
            name="descriptionEn"
            type="text"
            multiline
            rows={3}
            value={formData.descriptionEn}
            onChange={handleInputChange}
            notched
            error={!!errors.descriptionEn}
            placeholder="Example: Homes Packages"
          />
          {errors.descriptionEn && (
            <p className="error-message">{errors.descriptionEn}</p>
          )}
        </FormControl>

        {/* وصف المعاينة بالعربي */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="previewDescAr"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            وصف المعاينة بالعربي (اختياري)
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="previewDescAr"
            name="previewDescAr"
            type="text"
            multiline
            rows={2}
            value={formData.previewDescAr}
            onChange={handleInputChange}
            notched
            placeholder="مثال: وصف مختصر للمعاينة"
          />
        </FormControl>

        {/* وصف المعاينة بالانجليزية */}
        <FormControl sx={{ width: "100%" }} variant="outlined">
          <InputLabel
            htmlFor="previewDescEn"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            وصف المعاينة بالانجليزية (اختياري)
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="previewDescEn"
            name="previewDescEn"
            type="text"
            multiline
            rows={2}
            value={formData.previewDescEn}
            onChange={handleInputChange}
            notched
            placeholder="Example: Short description for preview"
          />
        </FormControl>

        <BlueButton
          text={currentView === "createList" ? "اضافة" : "تحديث"}
          size="medium"
          variant="fill"
          type="submit"
          disabled={loading}
          icon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        />
      </form>
    </div>
  );

  const renderDefaultView = () => <div></div>;

  const views = {
    createList: renderListForm,
    editList: renderListForm,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
